import { useState, type ReactNode } from 'react';
import { Modal, Button, Alert, Upload, useApp, theme, Segmented } from '../ui';
import { Upload as UploadIcon } from 'lucide-react';
import { api } from '../api/client';
import { downloadCsv } from '../lib/downloadCsv';

/**
 * Generic bulk-import modal used on each entity's own management page (Products, Farmers, Territories, …).
 * Flow: pick a CSV/Excel → Validate (dry-run, writes nothing) → review the preview → Import the valid rows.
 * `kind` maps to POST /imports/{kind} and GET /imports/templates/{kind}.csv.
 *
 * When `allowUpsert` is set (farmers, retailers) the user can choose "Add + Update existing": a row whose
 * phone matches an existing record OVERWRITES it (never the phone itself) instead of being skipped.
 */
export function BulkImportModal({
  open, onClose, onDone, kind, entityPlural, columns, note, extraControl, extraParams, allowUpsert,
}: {
  open: boolean;
  onClose: () => void;
  onDone: () => void;
  kind: string;            // e.g. 'products'
  entityPlural: string;    // e.g. 'products' — for button/label copy
  columns: string;         // e.g. 'sku, name, hsnCode, …'
  note?: ReactNode;        // extra hint under the columns line
  extraControl?: ReactNode;                  // rendered in the upload step (e.g. a fallback-owner picker)
  extraParams?: () => Record<string, any>;   // merged into the POST /imports/{kind} query params
  allowUpsert?: boolean;   // show the Add / Add + Update toggle (import supports overwrite-by-phone)
}) {
  const { token } = theme.useToken();
  const { message } = useApp();
  const [file, setFile] = useState<File | null>(null);
  const [mode, setMode] = useState<'add' | 'upsert'>('add');
  const [preview, setPreview] = useState<any>(null);
  const [result, setResult] = useState<any>(null);
  const [busy, setBusy] = useState(false);

  const reset = () => { setFile(null); setMode('add'); setPreview(null); setResult(null); setBusy(false); };
  const close = () => { reset(); onClose(); };

  const run = async (dryRun: boolean) => {
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const r = await api.post(`/imports/${kind}`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        params: { dryRun: dryRun ? 1 : 0, ...(allowUpsert && mode === 'upsert' ? { mode: 'upsert' } : {}), ...(extraParams?.() ?? {}) },
      });
      const data = r.data?.data ?? r.data;
      if (dryRun) setPreview(data);
      else { setResult(data); onDone(); }
    } catch (e: any) {
      message.error(e.response?.data?.error?.message ?? 'Import failed');
    } finally {
      setBusy(false);
    }
  };

  // An import that reports created/updated separately (farmers, retailers) — drives the richer breakdown.
  const isUpsertShape = (d: any) => d && (d.created != null || d.updated != null);
  const commitLabel = !preview
    ? ''
    : isUpsertShape(preview)
      ? (preview.success === 0
          ? 'Nothing to change'
          : `Apply ${preview.success} change${preview.success === 1 ? '' : 's'}`)
      : `Import ${preview.success} ${entityPlural}`;

  const footer = result
    ? [<Button key="done" variant="primary" onClick={close}>Done</Button>]
    : preview
      ? [
          <Button key="back" variant="default" onClick={() => setPreview(null)}>Back</Button>,
          <Button key="commit" variant={preview.failed > 0 ? 'danger' : 'primary'} loading={busy} disabled={!preview.success} onClick={() => run(false)}>
            {commitLabel}
          </Button>,
        ]
      : [
          <Button key="cancel" variant="default" onClick={close}>Cancel</Button>,
          <Button key="validate" variant="primary" loading={busy} disabled={!file} onClick={() => run(true)}>Validate</Button>,
        ];

  // A row of coloured count chips: New / Updated / Unchanged / Skipped / Errors — only the relevant ones show.
  const chip = (label: string, n: number, bg: string, fg: string, border: string) => (
    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'baseline', padding: '2px 10px', borderRadius: 999, background: bg, color: fg, border: `1px solid ${border}`, fontSize: 12.5, fontWeight: 600 }}>
      <span style={{ fontSize: 14 }}>{n}</span>{label}
    </span>
  );
  const counts = (d: any) => {
    const upsert = isUpsertShape(d);
    const created = upsert ? (d.created ?? 0) : (d.success ?? 0);
    const updated = d.updated ?? 0;
    const unchanged = d.unchanged ?? 0;
    const skipped = d.skipped ?? 0;
    // `failed` here = hard errors only. In non-upsert imports the backend rolls skips into `failed`, so show it as "skipped".
    const failed = d.failed ?? 0;
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
        {chip('new', created, token.colorSuccessBg, token.colorSuccessText, token.colorSuccessBorder)}
        {upsert && chip('updated', updated, token.colorInfoBg, token.colorInfoText, token.colorInfoBorder)}
        {upsert && unchanged > 0 && chip('unchanged', unchanged, token.colorFillSecondary, token.colorTextSecondary, token.colorBorder)}
        {(upsert ? skipped > 0 : false) && chip('skipped', skipped, token.colorWarningBg, token.colorWarningText, token.colorWarningBorder)}
        {failed > 0 && chip(upsert ? 'can’t import' : 'skipped', failed, token.colorErrorBg, token.colorErrorText, token.colorErrorBorder)}
      </div>
    );
  };

  const errorList = (data: any) => (data?.errors?.length ? (
    <div style={{ marginTop: 10, border: `1px solid ${token.colorBorderSecondary}`, borderRadius: 8, fontSize: 12.5, overflow: 'hidden' }}>
      <div style={{ padding: '6px 10px', fontWeight: 600, background: token.colorFillQuaternary, color: token.colorText }}>
        {data.errors.length} row{data.errors.length === 1 ? '' : 's'} not imported — why:
      </div>
      <div style={{ maxHeight: 180, overflow: 'auto' }}>
        {data.errors.map((e: any, i: number) => (
          <div key={i} style={{ padding: '5px 10px', borderTop: `1px solid ${token.colorFillQuaternary}` }}>
            <b>Row {e.row}:</b> {e.message}
          </div>
        ))}
      </div>
    </div>
  ) : null);

  // Aggregate, non-error notes about the import (e.g. how owners resolve for farmers). Preserves the
  // leading spaces the backend uses to indent breakdown lines.
  const infoList = (data: any) => (data?.info?.length ? (
    <div style={{ marginTop: 10, border: `1px solid ${token.colorInfoBorder}`, background: token.colorInfoBg, borderRadius: 8, fontSize: 12.5, padding: '8px 10px' }}>
      {data.info.map((line: string, i: number) => (
        <div key={i} style={{ whiteSpace: 'pre-wrap', color: token.colorText }}>{line}</div>
      ))}
    </div>
  ) : null);

  // Non-blocking heads-up (e.g. a same-name territory under a different code) — these rows still import.
  const warningList = (data: any) => (data?.warnings?.length ? (
    <div style={{ marginTop: 10, maxHeight: 160, overflow: 'auto', border: `1px solid ${token.colorWarningBorder}`, background: token.colorWarningBg, borderRadius: 8, fontSize: 12.5 }}>
      <div style={{ padding: '5px 10px', fontWeight: 600, color: token.colorWarningText, borderBottom: `1px solid ${token.colorWarningBorder}` }}>
        {data.warnings.length} possible duplicate{data.warnings.length === 1 ? '' : 's'} — these still import
      </div>
      {data.warnings.map((w: any, i: number) => (
        <div key={i} style={{ padding: '5px 10px', color: token.colorWarningText }}>
          <b>Row {w.row}:</b> {w.message}
        </div>
      ))}
    </div>
  ) : null);

  return (
    <Modal open={open} onOpenChange={(o) => { if (!o) close(); }} title={`Import ${entityPlural}`} footer={footer} width={560}>
      {!preview && !result && (
        <>
          <Alert type="info" showIcon style={{ marginBottom: 12 }}
            message={`Columns: ${columns}`}
            description={<span>{note} Nothing is written until you confirm the preview. <a style={{ cursor: 'pointer' }} onClick={() => downloadCsv(`/imports/templates/${kind}.csv`, `${kind}-template.csv`).catch(() => message.error('Template unavailable'))}>Download template</a></span>} />
          {allowUpsert && (
            <div style={{ marginBottom: 12 }}>
              <Segmented block value={mode} onChange={(v) => setMode(v as 'add' | 'upsert')}
                options={[
                  { label: 'Add new only', value: 'add' },
                  { label: 'Add + Update existing', value: 'upsert' },
                ]} />
              <div style={{ fontSize: 12, color: token.colorTextSecondary, marginTop: 6 }}>
                {mode === 'add'
                  ? 'Rows whose phone number already exists are skipped — nothing existing is changed.'
                  : 'Rows are matched to existing records by phone number and overwritten with the file’s values. The phone number itself is never changed and nothing is ever deleted; blank cells leave the existing value as-is.'}
              </div>
            </div>
          )}
          <Upload.Dragger
            accept=".csv,.xlsx,.xls"
            multiple={false}
            maxCount={1}
            beforeUpload={(f) => { setFile(f); return false; }}
            onRemove={() => setFile(null)}
            fileList={file ? ([{ uid: '1', name: file.name }] as any) : []}
          >
            <p style={{ margin: 0 }}><UploadIcon size={22} style={{ opacity: 0.6 }} /></p>
            <p style={{ margin: '6px 0 0' }}>Click or drop a CSV / Excel file</p>
          </Upload.Dragger>
          {extraControl ? <div style={{ marginTop: 12 }}>{extraControl}</div> : null}
        </>
      )}
      {preview && !result && (
        <>
          <Alert type={preview.failed > 0 ? 'warning' : 'success'} showIcon
            message={isUpsertShape(preview)
              ? `Preview — of ${preview.total} row${preview.total === 1 ? '' : 's'}, here’s what will happen`
              : `Preview — ${preview.success} will import, ${preview.failed} skipped (of ${preview.total})`}
            description="Nothing has been written yet. Import to commit the valid rows." />
          {isUpsertShape(preview) && counts(preview)}
          {infoList(preview)}
          {errorList(preview)}
          {warningList(preview)}
        </>
      )}
      {result && (() => {
        const notImported = [
          result.failed > 0 ? `${result.failed} couldn’t be imported` : '',
          result.skipped > 0 ? `${result.skipped} skipped` : '',
        ].filter(Boolean).join(', ');
        return (
          <>
            <Alert type="success" showIcon
              message={isUpsertShape(result)
                ? `Done — ${result.created ?? 0} added, ${result.updated ?? 0} updated${result.unchanged ? `, ${result.unchanged} unchanged` : ''} (of ${result.total})`
                : `Imported ${result.success} of ${result.total} ${entityPlural}`}
              description={notImported ? `${notImported} — see the reasons below.` : 'All rows imported.'} />
            {isUpsertShape(result) && counts(result)}
            {errorList(result)}
            {warningList(result)}
          </>
        );
      })()}
    </Modal>
  );
}
